import React from "react";

import {
  faBoxArchive,
  faCheck,
  faCheckCircle,
  faChevronDown,
  faChevronRight,
  faCopy,
  faLink,
  faRotateRight,
  faTableLayout,
} from "@fortawesome/pro-regular-svg-icons";
import { yupResolver } from "@hookform/resolvers/yup";
import { AxiosError, isAxiosError } from "axios";
import { isEqual } from "lodash-es";
import { FieldErrors, FormProvider, useForm, useWatch } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { ObjectSchema } from "yup";

import { Button } from "src/base-components/Button";
import { FixedPositionedDropdown } from "src/base-components/FixedPositionedDropDown";
import { Icon } from "src/base-components/Icon";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import {
  MENU_DIVIDER,
  OptionsDropdownElement,
} from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { Pill } from "src/base-components/Pill";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import {
  useCaseTemplateVersion,
  useCaseTemplateWithVersions,
  useUpdateCaseTemplateVersion,
} from "src/cases/queries/templates";
import { ArchiveTemplateVersionModal } from "src/cases/templates/ArchiveTemplateVersionModal";
import { CaseTemplateFormRouter } from "src/cases/templates/CaseTemplateFormRouter";
import { CaseTemplateFormSidebar } from "src/cases/templates/CaseTemplateFormSidebar";
import {
  CaseTemplatesWrapper,
  MENUBAR_WIDTH_KEY,
} from "src/cases/templates/CaseTemplatesWrapper";
import { BlockConfigSidebar } from "src/cases/templates/caseBlocks/BlockConfigSidebar";
import { CaseBlocksConfigProvider } from "src/cases/templates/caseBlocks/CaseBlocksConfigContext";
import { DuplicateTemplateVersionModal } from "src/cases/templates/DuplicateTemplateVersionModal";
import { useCurrentStep } from "src/cases/templates/formSteps";
import { getFirstErrorStep } from "src/cases/templates/formUtils/errorNavigation";
import { InvalidReferenceProvider } from "src/cases/templates/formUtils/InvalidReferenceContext";
import { caseTemplateFormSchema } from "src/cases/templates/formUtils/schema";
import { transformFormValuesToTemplate } from "src/cases/templates/formUtils/toBackendValues";
import { CaseTemplateFormValues } from "src/cases/templates/formUtils/types";
import { useCaseTemplateVersionForm } from "src/cases/templates/hooks";
import { PublishTemplateVersionModal } from "src/cases/templates/PublishTemplateVersionModal";
import {
  TemplateVersionName,
  TemplateVersionStatusIndicator,
} from "src/cases/templates/TemplateVersionName";
import { useIsTemplateVersionPublished } from "src/cases/templates/utils";
import {
  CaseTemplateVersionPublishState,
  JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel,
} from "src/clients/case-management/api";
import { EmptyState } from "src/design-system/EmptyState";
import { useModal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { WhitePane } from "src/jobs/common/WhitePane";
import { PageHeader } from "src/layout/PageHeader";
import {
  CaseTemplateVersionPageParams,
  DashboardPageParams,
  getBaseUrl,
  getUrlToCaseTemplatesPage,
  getUrlToCaseTemplateVersionPage,
  getUrlToCaseTemplateVersionsPage,
} from "src/router/urls";
import { copyTextToClipboard } from "src/utils/clipboard";
import { dateFromNow } from "src/utils/datetime";
import { ErrorBoundary } from "src/utils/ErrorBoundary";
import { logger } from "src/utils/logger";
import { assertUnreachable } from "src/utils/typeUtils";
import { useParamsDecode } from "src/utils/useParamsDecode";

const CaseTemplateVersionContent: React.FC = () => {
  const { orgId, wsId, templateId, versionId } =
    useParamsDecode<CaseTemplateVersionPageParams>();

  const templateQuery = useCaseTemplateWithVersions(templateId);
  const templateVersionQuery = useCaseTemplateVersionForm(
    templateId,
    versionId,
  );

  const {
    mutateAsync: updateCaseTemplateVersion,
    isPending: isUpdating,
    isError: isUpdateError,
  } = useUpdateCaseTemplateVersion();

  const form = useForm<CaseTemplateFormValues>({
    mode: "onBlur",
    shouldUnregister: false,
    values: !isUpdateError ? templateVersionQuery.data : undefined,
    resolver: yupResolver<CaseTemplateFormValues>(
      // This is very unsafe type casting, when changing this form data structure
      // We need to make sure that validation schema is working correctly.
      // This casting is hiding the actual data structure
      // It is very hard to map types to the validation schema
      // because we do not handle every field in the schema.
      // And template version structure is very complex and deep data structure.
      caseTemplateFormSchema as ObjectSchema<CaseTemplateFormValues>,
    ),
  });

  const [currentStepId, setCurrentStep] = useCurrentStep();

  const originalData = templateVersionQuery.data;

  // Simple deep comparison that handles arrays properly
  // Transform both to the same format (API format) for comparison
  const isFormDirty = useWatch<CaseTemplateFormValues>({
    control: form.control,
    compute: (form) => {
      if (!originalData || !form) return false;

      if (!form.attributes || !originalData.attributes) return false;

      try {
        const transformedCurrent = transformFormValuesToTemplate(form);
        const transformedOriginal = transformFormValuesToTemplate(originalData);

        return !isEqual(transformedCurrent, transformedOriginal);
      } catch (error) {
        logger.error("Error comparing form values", error);

        return false;
      }
    },
  });

  const navigate = useNavigate();

  const {
    isOpen: isDuplicateModalOpen,
    openModal: openDuplicateModal,
    closeModal: closeDuplicateModal,
  } = useModal<string>();
  const {
    isOpen: isPublishModalOpen,
    data: versionToPublish,
    openModal: openPublishModal,
    closeModal: closePublishModal,
  } = useModal<JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel>();
  const {
    isOpen: isArchiveModalOpen,
    data: versionToArchive,
    openModal: openArchiveModal,
    closeModal: closeArchiveModal,
  } = useModal<JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel>();

  const onSubmit = async (data: CaseTemplateFormValues) => {
    try {
      await updateCaseTemplateVersion({
        templateId,
        versionId,
        versionAttributes: transformFormValuesToTemplate(data).attributes,
        etag: templateVersionQuery.data?.attributes.etag,
      });

      toastActions.success({
        title: "Template version updated successfully",
      });
    } catch (error) {
      logger.error("Error updating template", error);

      if (isAxiosError(error) && error.response?.status === 412) {
        toastActions.failure({
          title: "Error updating template",
          description:
            "The template has been updated since you last loaded it. Please refresh the page and try again.",
        });
        return;
      }

      if (isAxiosError(error)) {
        toastActions.failure({
          title: "Error updating template",
          description:
            error.response?.data.errors[0]?.detail ?? "Unknown error",
        });
        return;
      }

      toastActions.failure({
        title: "Error updating template",
        description:
          "Unexpected error occurred while updating the template. Please try again later.",
      });
    }
  };

  const onInvalid = (errors: FieldErrors<CaseTemplateFormValues>) => {
    const errorStep = getFirstErrorStep(errors);
    if (errorStep && errorStep !== currentStepId) {
      setCurrentStep(errorStep);
      toastActions.warning({
        title: "Please fix validation errors",
        description:
          "There are validation errors that need to be fixed before saving.",
      });
    }
  };

  const getPageHeaderTitle = () => {
    if (templateQuery.isLoading) {
      return (
        <div className="flex items-center gap-x-2">
          <SkeletonPlaceholder height="h-5" width="w-36" />
          <Icon icon={faChevronRight} size="xs" />
          <SkeletonPlaceholder height="h-5" width="w-36" />
        </div>
      );
    }

    if (templateQuery.isSuccess) {
      return (
        <div className="flex items-center gap-x-2">
          <Link
            className="flex items-center gap-x-2"
            to={getUrlToCaseTemplateVersionsPage(orgId, wsId, templateId)}
          >
            <div className="max-w-96 truncate">
              {templateQuery.data.attributes.template_name}
            </div>{" "}
            <span className="whitespace-nowrap">Template Configuration</span>
          </Link>
          <Icon icon={faChevronRight} size="xs" />
          <VersionPicker versions={templateQuery.data.versions ?? []} />
        </div>
      );
    }

    const isNotFound = templateQuery.error?.response?.status === 404;

    return isNotFound ? "Template not found" : "Error loading template";
  };

  return (
    <div
      className="flex h-full flex-col bg-gray-50"
      data-loc="case-template-version-page"
    >
      <FormProvider {...form}>
        <form
          className="flex h-full flex-col"
          onSubmit={form.handleSubmit(onSubmit, onInvalid)}
        >
          <PageHeader
            backLink={getUrlToCaseTemplateVersionsPage(orgId, wsId, templateId)}
            sideContent={
              <div className="flex items-center gap-x-3">
                {templateVersionQuery.isLoading ? (
                  <SkeletonPlaceholder height="h-5" width="w-96" />
                ) : (
                  <UpdatedInfo version={templateVersionQuery.data} />
                )}
                <Button
                  disabled={templateVersionQuery.isFetching || !isFormDirty}
                  htmlType="submit"
                  loading={isUpdating}
                  size="sm"
                >
                  Save changes
                </Button>
                <PageMenu
                  onClickArchive={openArchiveModal}
                  onClickDuplicate={openDuplicateModal}
                  onClickPublish={openPublishModal}
                />
              </div>
            }
            title={getPageHeaderTitle()}
          />

          {templateVersionQuery.error ? (
            <CaseTemplateErrorState
              error={templateVersionQuery.error}
              refetch={templateVersionQuery.refetch}
            />
          ) : (
            <ErrorBoundary
              fallbackErrorContent={({ resetErrorBoundary }) => (
                <FormErrorFallback onRetry={resetErrorBoundary} />
              )}
            >
              <CaseBlocksConfigProvider>
                <InvalidReferenceProvider>
                  <CaseTemplatesWrapper>
                    <div
                      className="flex-none"
                      style={{ width: `var(${MENUBAR_WIDTH_KEY})` }}
                    >
                      <CaseTemplateFormSidebar />
                    </div>
                    <div className="isolate w-194">
                      <CaseTemplateFormRouter
                        isLoading={templateVersionQuery.isLoading}
                      />
                    </div>
                    <BlockConfigSidebar />
                  </CaseTemplatesWrapper>
                </InvalidReferenceProvider>
              </CaseBlocksConfigProvider>
            </ErrorBoundary>
          )}
        </form>
      </FormProvider>

      <DuplicateTemplateVersionModal
        open={isDuplicateModalOpen}
        versionId={versionId}
        onClose={closeDuplicateModal}
        onDuplicateSuccess={(data) => {
          navigate(
            getUrlToCaseTemplateVersionPage(orgId, wsId, templateId, data.id),
          );
        }}
      />

      <PublishTemplateVersionModal
        open={isPublishModalOpen}
        templateId={templateId}
        version={versionToPublish ?? null}
        onClose={closePublishModal}
      />

      <ArchiveTemplateVersionModal
        open={isArchiveModalOpen}
        templateId={templateId}
        version={versionToArchive ?? null}
        onArchiveSuccess={() => {
          navigate(getUrlToCaseTemplateVersionsPage(orgId, wsId, templateId));
        }}
        onClose={closeArchiveModal}
      />
    </div>
  );
};

export const CaseTemplateEditPage: React.FC = () => {
  useDocumentTitle(["Edit Template", "Case Templates"]);

  return <CaseTemplateVersionContent />;
};

export const CaseTemplateErrorState: React.FC<{
  error: AxiosError;
  refetch: () => void;
}> = ({ error, refetch }) => {
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const navigate = useNavigate();
  const isNotFoundError = error.response?.status === 404;

  if (isNotFoundError) {
    return (
      <div className="m-auto flex w-[1084px] flex-1 flex-start gap-x-5 bg-gray-50 py-10">
        <WhitePane>
          <EmptyState
            actionButton={{
              text: "Go to Case templates",
              onClick: () => navigate(getUrlToCaseTemplatesPage(orgId, wsId)),
            }}
            description="The template you are looking for does not exist."
            headline="Template not found"
            icon={faTableLayout}
            variant="error"
          />
        </WhitePane>
      </div>
    );
  }

  return (
    <div className="m-auto flex w-[1084px] flex-1 flex-start gap-x-5 bg-gray-50 py-10">
      <WhitePane>
        <EmptyState
          actionButton={{
            icon: faRotateRight,
            text: "Retry",
            onClick: refetch,
          }}
          description="We're having trouble loading case template. Please try again later or contact Taktile support if the problem persists."
          headline="Error loading template"
          icon={faTableLayout}
          variant="error"
        />
      </WhitePane>
    </div>
  );
};

type VersionPickerProps = {
  versions: JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel[];
};

export const VersionPicker: React.FC<VersionPickerProps> = ({ versions }) => {
  const navigate = useNavigate();
  const { orgId, wsId, templateId, versionId } =
    useParamsDecode<CaseTemplateVersionPageParams>();

  return (
    <FixedPositionedDropdown
      buttonClassName="rounded-full border-none max-w-64"
      dataLoc="current-version-picker"
      elements={versions.map((version) => ({
        key: version.id,
        value: version,
      }))}
      placement="bottom start"
      renderButtonValue={(version) =>
        version ? (
          <Pill
            dataLoc="current-version-picker-button"
            size="lg"
            variant="outlined-white"
            fullWidth
          >
            <TemplateVersionStatusIndicator
              state={version.attributes.publish_state}
            />
            <Pill.Text>{version.attributes.version_name}</Pill.Text>
            <Pill.Icon icon={faChevronDown} size="base" />
          </Pill>
        ) : (
          <SkeletonPlaceholder height="h-5" width="w-36" />
        )
      }
      renderValue={({ value: version, selected }) => (
        <div className="flex items-center justify-between gap-x-1">
          <TemplateVersionName
            name={version.attributes.version_name}
            state={version.attributes.publish_state}
          />
          {selected && (
            <Icon color="text-indigo-500" icon={faCheck} size="sm" />
          )}
        </div>
      )}
      selected={versionId}
      onSelect={(versionId) =>
        navigate(
          getUrlToCaseTemplateVersionPage(orgId, wsId, templateId, versionId),
        )
      }
    />
  );
};

const UpdatedInfo: React.FC<{
  version: CaseTemplateFormValues | undefined;
}> = ({ version }) => {
  if (!version) {
    return null;
  }

  return (
    <Pill
      dataLoc="last-updated-info"
      variant={getPillColor(version.attributes.publish_state)}
    >
      <Pill.Icon icon={faCheck} />
      <Pill.Text fontType="text">
        {getStatusPrefix(version.attributes.publish_state)}{" "}
        {dateFromNow(version.attributes.updated_at)}
      </Pill.Text>
    </Pill>
  );
};

const getPillColor = (status: CaseTemplateVersionPublishState) => {
  switch (status) {
    case CaseTemplateVersionPublishState.DRAFT:
      return "indigo-light";
    case CaseTemplateVersionPublishState.PUBLISHED:
      return "green";
    default:
      assertUnreachable(status);
  }
};

const getStatusPrefix = (status: CaseTemplateVersionPublishState) => {
  switch (status) {
    case CaseTemplateVersionPublishState.DRAFT:
      return "Updated";
    case CaseTemplateVersionPublishState.PUBLISHED:
      return "Published";
    default:
      assertUnreachable(status);
  }
};

const FormErrorFallback: React.FC<{
  onRetry: () => void;
}> = ({ onRetry }) => {
  return (
    <div className="m-auto flex w-[1084px] flex-1 flex-start gap-x-5 bg-gray-50 py-10">
      <WhitePane>
        <EmptyState
          actionButton={{
            icon: faRotateRight,
            text: "Try to restore page",
            onClick: onRetry,
          }}
          description="Something went wrong while displaying the template form. Please try again."
          headline="Error displaying template"
          icon={faTableLayout}
          variant="error"
        />
      </WhitePane>
    </div>
  );
};

const PageMenu: React.FC<{
  onClickDuplicate: (versionId: string) => void;
  onClickPublish: (
    version: JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel,
  ) => void;
  onClickArchive: (
    version: JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel,
  ) => void;
}> = ({ onClickDuplicate, onClickPublish, onClickArchive }) => {
  const { orgId, wsId, templateId, versionId } =
    useParamsDecode<CaseTemplateVersionPageParams>();
  const { data: template } = useCaseTemplateWithVersions(templateId);
  const { data: templateVersion } = useCaseTemplateVersion({
    templateId,
    versionId,
  });
  const isPublishedVersion = useIsTemplateVersionPublished();
  const { caseTemplates } = useCapabilities();

  const templateActiveVersions =
    template?.versions?.filter((v) => v.attributes.archived === false) ?? [];

  const handlePublish = (
    version: JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel,
  ) => {
    if (
      version.attributes.publish_state ===
      CaseTemplateVersionPublishState.PUBLISHED
    ) {
      logger.error("Cannot publish published version", version);
      return;
    }

    onClickPublish(version);
  };

  const handleArchive = (
    version: JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel,
  ) => {
    onClickArchive(version);
  };

  const options = [
    {
      key: "Duplicate",
      icon: faCopy,
      action: () => onClickDuplicate(versionId),
      disabled:
        templateActiveVersions.length >= 10
          ? "You've reached the limit of 10 versions for this template. To create a new version, please delete an existing one."
          : undefined,
    },
    {
      key: "Copy link",
      icon: faLink,
      action: () => {
        copyTextToClipboard(
          getBaseUrl() +
            getUrlToCaseTemplateVersionPage(orgId, wsId, templateId, versionId),
        );

        toastActions.success({
          title: "Link copied to clipboard",
        });
      },
    },
    !isPublishedVersion && MENU_DIVIDER,
    !isPublishedVersion &&
      templateVersion && {
        key: "Publish",
        icon: faCheckCircle,
        action: () => handlePublish(templateVersion),
      },
    templateVersion &&
      (!isPublishedVersion || caseTemplates.canArchivePublished) &&
      MENU_DIVIDER,
    templateVersion &&
      (!isPublishedVersion || caseTemplates.canArchivePublished) && {
        key: "Archive",
        icon: faBoxArchive,
        action: () => handleArchive(templateVersion),
        disabled:
          templateActiveVersions.length <= 1
            ? "You cannot archive the last version"
            : undefined,
      },
  ].filter(Boolean) as OptionsDropdownElement[];

  return (
    <EllipsisOptionsDropdown dataLoc="version-page-menu" elements={options} />
  );
};
